import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 1,
  class: "text-grey"
}
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "text-caption text-grey mt-1" }
const _hoisted_5 = ["colspan"]
const _hoisted_6 = { class: "d-flex flex-wrap ga-2" }

import { ref, computed } from 'vue'

interface Equipment {
  id: string
  equipment_type: string
  rating: number | null
  manufacturer: string
  model: string
  status: string
  location: string
  cpu?: string
  ram?: string
  storage?: string
  gpu?: string
  os?: string
  screen?: string
  estimated_value?: number
  value?: number | null
  donor?: string
  donated_on?: string | null
  comments?: string
  claimed_by?: string
  claimed_for?: string
}

interface Props {
  equipment: Equipment[]
  selectedItems?: string[]
  loading?: boolean
  isAdmin?: boolean
  requestMode?: boolean
  itemsPerPage?: number
  requestData?: any  // The request object with needed_for tasks
}


export default /*@__PURE__*/_defineComponent({
  __name: 'InventoryTable',
  props: {
    equipment: {},
    selectedItems: { default: () => [] },
    loading: { type: Boolean, default: false },
    isAdmin: { type: Boolean, default: false },
    requestMode: { type: Boolean, default: false },
    itemsPerPage: { default: 25 },
    requestData: { default: null }
  },
  emits: ['update:selectedItems', 'edit', 'delete', 'claim', 'unclaim', 'bulkEdit', 'printPackingSlips', 'printDonationReceipts', 'addMoreOfModel'],
  setup(__props: any, { emit: __emit }) {

/* eslint-disable no-undef */
const props = __props

const emit = __emit

const expandedItems = ref<any[]>([])

const selectedItemsModel = computed({
  get: () => props.selectedItems,
  set: (value) => emit('update:selectedItems', value)
})

const toggleRowExpansion = (event: any, { item }: any) => {
  // VDataTable passes the raw item
  const itemId = item.id
  const index = expandedItems.value.findIndex((id: any) => id === itemId)

  if (index > -1) {
    // Item is expanded, collapse it
    expandedItems.value.splice(index, 1)
  } else {
    // Item is collapsed, expand it
    expandedItems.value.push(itemId)
  }
}

const headers = computed(() => {
  const baseHeaders = [
    { title: 'Asset Tag', key: 'asset_tag', sortable: true },
    { title: 'Type', key: 'equipment_type', sortable: true },
    { title: 'Rating', key: 'rating', sortable: true },
    { title: 'Brand/Model', key: 'manufacturer', sortable: true },
    { title: 'Status', key: 'status', sortable: true },
    { title: 'Location', key: 'location', sortable: true }
  ]

  if (!props.requestMode) {
    baseHeaders.push({ title: 'Donor', key: 'donor', sortable: true })
  }

  // if (props.requestMode) {
  //   baseHeaders.push({ title: 'Claimed', key: 'claimed', sortable: true })
  // }

  if (props.requestMode || props.isAdmin) {
    baseHeaders.push({ title: 'Actions', key: 'actions', sortable: false })
  }

  return baseHeaders
})

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount)
}

const formatDate = (value?: string | null) => {
  if (!value) return 'N/A'
  const date = new Date(value)
  if (isNaN(date.getTime())) return 'N/A'
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatStatus = (status: string) => {
  if (!status) return 'Unknown'
  return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')
}

const getRatingColor = (rating: number | null) => {
  if (!rating) return 'grey'
  // Yellow to green gradient: lower ratings are more yellow, higher are more green
  if (rating >= 8) return 'success' // Green
  if (rating >= 6) return 'light-green' // Light green
  if (rating >= 4) return 'lime' // Lime (yellow-green)
  return 'yellow-darken-2' // Yellow
}

const getStatusColor = (status: string) => {
  const statusColors: Record<string, string> = {
    processing: 'info',
    troubleshooting: 'warning',
    parts: 'deep-orange-darken-2',
    available: 'success',
    in_inventory: 'success',
    delivered: 'info',
    maintenance: 'warning',
    retired: 'error',
    claimed: 'warning'
  }
  return statusColors[status] || 'grey'
}

// Task capability checking based on equipment rating
const getMinimumRatingForTask = (task: string): number => {
  const taskRequirements: Record<string, number> = {
    email: 1,
    web_browsing: 1,
    office_work: 1,
    document_editing: 1,
    video_conferencing: 4,
    presentations: 4,
    coding: 4,
    photo_editing: 6,
    music_production: 6,
    video_editing: 8,
    '3d_modeling': 8,
    gaming: 8,
    creative_work: 8,
    animation: 8,
    machine_learning: 8,
    cad: 8
  }
  return taskRequirements[task] || 1
}

const isTaskSupported = (equipment: any, task: string): boolean => {
  const rating = equipment.rating || 0
  const minRating = getMinimumRatingForTask(task)
  return rating >= minRating
}

const formatTaskName = (task: string): string => {
  const taskLabels: Record<string, string> = {
    email: 'Email',
    web_browsing: 'Web Browsing',
    office_work: 'Office Work',
    document_editing: 'Document Editing',
    video_conferencing: 'Video Conferencing',
    presentations: 'Presentations',
    coding: 'Programming/Coding',
    photo_editing: 'Photo Editing',
    video_editing: 'Video Editing',
    '3d_modeling': '3D Modeling',
    gaming: 'Gaming',
    creative_work: 'Creative Work',
    machine_learning: 'Machine Learning',
    cad: 'CAD',
    music_production: 'Music Production',
    animation: 'Animation'
  }
  return taskLabels[task] || task.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

return (_ctx: any,_cache: any) => {
  const _component_v_toolbar_title = _resolveComponent("v-toolbar-title")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_data_table = _resolveComponent("v-data-table")!

  return (_openBlock(), _createBlock(_component_v_card, null, {
    default: _withCtx(() => [
      (!__props.requestMode && selectedItemsModel.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_toolbar, {
            key: 0,
            color: "primary",
            dark: "",
            density: "compact"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_toolbar_title, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(selectedItemsModel.value.length) + " item(s) selected", 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_spacer),
              (__props.isAdmin)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 0,
                    variant: "text",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('bulkEdit', selectedItemsModel.value))),
                    "prepend-icon": "mdi-pencil"
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode(" Bulk Edit ", -1)
                    ]))]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_component_v_btn, {
                variant: "text",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('printPackingSlips', selectedItemsModel.value))),
                "prepend-icon": "mdi-printer"
              }, {
                default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                  _createTextVNode(" Print Packing Slips ", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_v_btn, {
                variant: "text",
                onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('printDonationReceipts', selectedItemsModel.value))),
                "prepend-icon": "mdi-receipt-text-outline"
              }, {
                default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                  _createTextVNode(" Print Receipts ", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_v_data_table, {
        headers: headers.value,
        items: __props.equipment,
        loading: __props.loading,
        "items-per-page": __props.itemsPerPage,
        "show-select": !__props.requestMode,
        modelValue: selectedItemsModel.value,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((selectedItemsModel).value = $event)),
        expanded: expandedItems.value,
        "onUpdate:expanded": _cache[6] || (_cache[6] = ($event: any) => ((expandedItems).value = $event)),
        "item-value": "id",
        class: "elevation-1 clickable-table",
        "onClick:row": toggleRowExpansion
      }, {
        "item.equipment_type": _withCtx(({ item }) => [
          _createVNode(_component_v_chip, {
            color: "secondary",
            size: "small"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString((item as any).equipment_type), 1)
            ]),
            _: 2
          }, 1024)
        ]),
        "item.rating": _withCtx(({ item }) => [
          _createVNode(_component_v_chip, {
            color: getRatingColor((item as any).rating),
            size: "small",
            variant: "flat",
            style: { opacity: 0.85 }
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString((item as any).rating || '-'), 1)
            ]),
            _: 2
          }, 1032, ["color"])
        ]),
        "item.manufacturer": _withCtx(({ item }) => [
          _createElementVNode("div", null, [
            _createElementVNode("strong", null, _toDisplayString((item as any).manufacturer || '-'), 1),
            _createTextVNode(" " + _toDisplayString((item as any).model ? ` ${(item as any).model}` : ''), 1)
          ])
        ]),
        "item.donor": _withCtx(({ item }) => [
          ((item as any).donor)
            ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString((item as any).donor), 1))
            : (_openBlock(), _createElementBlock("span", _hoisted_2, "-"))
        ]),
        "item.status": _withCtx(({ item }) => [
          _createVNode(_component_v_chip, {
            color: getStatusColor((item as any).status),
            size: "small",
            variant: "tonal"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(formatStatus((item as any).status)), 1)
            ]),
            _: 2
          }, 1032, ["color"])
        ]),
        "item.claimed": _withCtx(({ item }) => [
          ((item as any).claimed_by)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(_component_v_chip, {
                  color: "warning",
                  size: "small",
                  variant: "tonal"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      start: "",
                      size: "small"
                    }, {
                      default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                        _createTextVNode("mdi-account-check", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[11] || (_cache[11] = _createTextVNode(" Claimed ", -1))
                  ]),
                  _: 1
                }),
                _createElementVNode("div", _hoisted_4, " For: " + _toDisplayString((item as any).claimed_for), 1)
              ]))
            : (_openBlock(), _createBlock(_component_v_chip, {
                key: 1,
                color: "success",
                size: "small",
                variant: "tonal"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, {
                    start: "",
                    size: "small"
                  }, {
                    default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                      _createTextVNode("mdi-check-circle", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[13] || (_cache[13] = _createTextVNode(" Available ", -1))
                ]),
                _: 1
              }))
        ]),
        "item.actions": _withCtx(({ item }) => [
          (__props.requestMode)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "d-flex ga-2",
                onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
              }, [
                (!(item as any).claimed_by)
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 0,
                      color: "success",
                      size: "small",
                      onClick: ($event: any) => (_ctx.$emit('claim', item))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { start: "" }, {
                          default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                            _createTextVNode("mdi-hand-extended", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[15] || (_cache[15] = _createTextVNode(" Claim ", -1))
                      ]),
                      _: 1
                    }, 8, ["onClick"]))
                  : (_openBlock(), _createBlock(_component_v_btn, {
                      key: 1,
                      color: "warning",
                      size: "small",
                      onClick: ($event: any) => (_ctx.$emit('unclaim', item))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { start: "" }, {
                          default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                            _createTextVNode("mdi-cancel", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[17] || (_cache[17] = _createTextVNode(" Unclaim ", -1))
                      ]),
                      _: 1
                    }, 8, ["onClick"]))
              ]))
            : (_openBlock(), _createElementBlock("div", {
                key: 1,
                class: "d-flex ga-2",
                onClick: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"]))
              }, [
                _createVNode(_component_v_btn, {
                  icon: "mdi-receipt-text-outline",
                  size: "small",
                  variant: "text",
                  color: "primary",
                  title: "Print donation receipt",
                  onClick: ($event: any) => (_ctx.$emit('printDonationReceipts', [(item as any).id]))
                }, null, 8, ["onClick"]),
                (!(item as any).claimed_by && (item as any).status === 'available')
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 0,
                      color: "info",
                      size: "small",
                      variant: "text",
                      onClick: ($event: any) => (_ctx.$emit('claim', item)),
                      title: "Claim by name"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, null, {
                          default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                            _createTextVNode("mdi-hand-extended", -1)
                          ]))]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["onClick"]))
                  : _createCommentVNode("", true),
                (__props.isAdmin)
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 1,
                      icon: "mdi-pencil",
                      size: "small",
                      variant: "text",
                      onClick: ($event: any) => (_ctx.$emit('edit', item))
                    }, null, 8, ["onClick"]))
                  : _createCommentVNode("", true),
                (__props.isAdmin)
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 2,
                      icon: "mdi-delete",
                      size: "small",
                      variant: "text",
                      color: "error",
                      onClick: ($event: any) => (_ctx.$emit('delete', item))
                    }, null, 8, ["onClick"]))
                  : _createCommentVNode("", true)
              ]))
        ]),
        "expanded-row": _withCtx(({ item }) => [
          _createElementVNode("tr", null, [
            _createElementVNode("td", {
              colspan: headers.value.length + 1
            }, [
              _createVNode(_component_v_card, {
                flat: "",
                class: "ma-4"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, { class: "text-h6" }, {
                    default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                      _createTextVNode("Equipment Details", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_card_text, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_row, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, {
                            cols: "12",
                            sm: "6",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _cache[20] || (_cache[20] = _createElementVNode("div", { class: "text-caption text-grey" }, "CPU", -1)),
                              _createElementVNode("div", null, _toDisplayString((item as any).cpu || 'N/A'), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            sm: "6",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _cache[21] || (_cache[21] = _createElementVNode("div", { class: "text-caption text-grey" }, "RAM", -1)),
                              _createElementVNode("div", null, _toDisplayString((item as any).ram || 'N/A'), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            sm: "6",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _cache[22] || (_cache[22] = _createElementVNode("div", { class: "text-caption text-grey" }, "Storage", -1)),
                              _createElementVNode("div", null, _toDisplayString((item as any).storage || 'N/A'), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            sm: "6",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _cache[23] || (_cache[23] = _createElementVNode("div", { class: "text-caption text-grey" }, "GPU", -1)),
                              _createElementVNode("div", null, _toDisplayString((item as any).gpu || 'N/A'), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            sm: "6",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _cache[24] || (_cache[24] = _createElementVNode("div", { class: "text-caption text-grey" }, "OS", -1)),
                              _createElementVNode("div", null, _toDisplayString((item as any).os || 'N/A'), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            sm: "6",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _cache[25] || (_cache[25] = _createElementVNode("div", { class: "text-caption text-grey" }, "Screen", -1)),
                              _createElementVNode("div", null, _toDisplayString((item as any).screen || 'N/A'), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            sm: "6",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _cache[26] || (_cache[26] = _createElementVNode("div", { class: "text-caption text-grey" }, "Value", -1)),
                              _createElementVNode("div", null, _toDisplayString((item as any).value != null ? formatCurrency((item as any).value) : 'N/A'), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            sm: "6",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _cache[27] || (_cache[27] = _createElementVNode("div", { class: "text-caption text-grey" }, "Donor", -1)),
                              _createElementVNode("div", null, _toDisplayString((item as any).donor || 'N/A'), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            sm: "6",
                            md: "4"
                          }, {
                            default: _withCtx(() => [
                              _cache[28] || (_cache[28] = _createElementVNode("div", { class: "text-caption text-grey" }, "Donated On", -1)),
                              _createElementVNode("div", null, _toDisplayString(formatDate((item as any).donated_on)), 1)
                            ]),
                            _: 2
                          }, 1024),
                          ((item as any).comments)
                            ? (_openBlock(), _createBlock(_component_v_col, {
                                key: 0,
                                cols: "12"
                              }, {
                                default: _withCtx(() => [
                                  _cache[29] || (_cache[29] = _createElementVNode("div", { class: "text-caption text-grey" }, "Comments", -1)),
                                  _createElementVNode("div", null, _toDisplayString((item as any).comments), 1)
                                ]),
                                _: 2
                              }, 1024))
                            : _createCommentVNode("", true),
                          (__props.requestMode && __props.requestData && __props.requestData.needed_for)
                            ? (_openBlock(), _createBlock(_component_v_col, {
                                key: 1,
                                cols: "12"
                              }, {
                                default: _withCtx(() => [
                                  _cache[32] || (_cache[32] = _createElementVNode("div", { class: "text-caption text-grey mb-2" }, "Task Compatibility", -1)),
                                  _createElementVNode("div", _hoisted_6, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.requestData.needed_for, (task) => {
                                      return (_openBlock(), _createBlock(_component_v_chip, {
                                        key: task,
                                        size: "small",
                                        color: isTaskSupported(item, task) ? 'success' : 'error',
                                        variant: isTaskSupported(item, task) ? 'flat' : 'tonal'
                                      }, {
                                        default: _withCtx(() => [
                                          (isTaskSupported(item, task))
                                            ? (_openBlock(), _createBlock(_component_v_icon, {
                                                key: 0,
                                                start: "",
                                                size: "small"
                                              }, {
                                                default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                                                  _createTextVNode("mdi-check", -1)
                                                ]))]),
                                                _: 1
                                              }))
                                            : (_openBlock(), _createBlock(_component_v_icon, {
                                                key: 1,
                                                start: "",
                                                size: "small"
                                              }, {
                                                default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                                                  _createTextVNode("mdi-close", -1)
                                                ]))]),
                                                _: 1
                                              })),
                                          _createTextVNode(" " + _toDisplayString(formatTaskName(task)), 1)
                                        ]),
                                        _: 2
                                      }, 1032, ["color", "variant"]))
                                    }), 128))
                                  ])
                                ]),
                                _: 2
                              }, 1024))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1024),
                  (!__props.requestMode)
                    ? (_openBlock(), _createBlock(_component_v_card_actions, { key: 0 }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_spacer),
                          _createVNode(_component_v_btn, {
                            color: "primary",
                            variant: "text",
                            onClick: ($event: any) => (_ctx.$emit('printDonationReceipts', [(item as any).id]))
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_icon, { start: "" }, {
                                default: _withCtx(() => [...(_cache[33] || (_cache[33] = [
                                  _createTextVNode("mdi-receipt-text-outline", -1)
                                ]))]),
                                _: 1
                              }),
                              _cache[34] || (_cache[34] = _createTextVNode(" Print Donation Receipt ", -1))
                            ]),
                            _: 1
                          }, 8, ["onClick"]),
                          (__props.isAdmin)
                            ? (_openBlock(), _createBlock(_component_v_btn, {
                                key: 0,
                                color: "primary",
                                variant: "outlined",
                                onClick: ($event: any) => (_ctx.$emit('addMoreOfModel', item))
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, { start: "" }, {
                                    default: _withCtx(() => [...(_cache[35] || (_cache[35] = [
                                      _createTextVNode("mdi-plus-circle", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _cache[36] || (_cache[36] = _createTextVNode(" Add More of This Model ", -1))
                                ]),
                                _: 1
                              }, 8, ["onClick"]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1024))
                    : _createCommentVNode("", true)
                ]),
                _: 2
              }, 1024)
            ], 8, _hoisted_5)
          ])
        ]),
        loading: _withCtx(() => [
          _createVNode(_component_v_skeleton_loader, { type: "table-row@10" })
        ]),
        "no-data": _withCtx(() => [
          _createVNode(_component_v_alert, {
            type: "info",
            variant: "tonal",
            class: "ma-4"
          }, {
            default: _withCtx(() => [...(_cache[37] || (_cache[37] = [
              _createTextVNode(" No equipment found ", -1)
            ]))]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["headers", "items", "loading", "items-per-page", "show-select", "modelValue", "expanded"])
    ]),
    _: 1
  }))
}
}

})